import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Flex, P, Icon, Tooltip } from '@allied-solutions/affinity'
import {
  Success,
  Close02,
  Error,
  Clock,
} from '@allied-solutions/affinity-react-icons/standard'

const coverageRequirementTooltipId = label => {
  const slug = String(label ?? '')
    .trim()
    .replaceAll(/\s+/g, '-')
    .replaceAll(/[^a-zA-Z0-9_-]/g, '')
  return `${slug || 'coverage-requirement'}-tooltip`
}

const coverageRequirementTooltipPopperOptions = { strategy: 'fixed' }

const REQUIREMENT_LABEL_OVERRIDES = {
  CoverageBaseAmount: 'Required Coverage Amount',
}

const formatRequirementLabel = label => {
  const text = String(label ?? '').trim()
  if (!text) return text
  if (REQUIREMENT_LABEL_OVERRIDES[text])
    return REQUIREMENT_LABEL_OVERRIDES[text]
  if (text.includes(' ')) return text

  return text
    .replaceAll(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replaceAll(/(?<=[A-Z])(?=[A-Z][a-z])/g, ' ')
}

const parseNumericAmount = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value !== 'string') return null

  const cleaned = value.replaceAll(/[$,]/g, '').trim()
  if (!cleaned || !/^-?\d+(\.\d+)?$/.test(cleaned)) return null

  const amount = Number(cleaned)
  return Number.isFinite(amount) ? amount : null
}

const formatCurrencyAmount = amount =>
  amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })

const formatRequirementValue = value => {
  if (value === null || value === undefined) return 'None provided'
  const amount = parseNumericAmount(value)
  return amount === null ? value : formatCurrencyAmount(amount)
}

const formatCurrencyInText = text => {
  if (!text || typeof text !== 'string') return text

  return text.replaceAll(/\$?\d[\d,]*(?:\.\d+)?/g, match => {
    const amount = parseNumericAmount(match)
    return amount === null ? match : formatCurrencyAmount(amount)
  })
}

const formatToolTip = (text, label, displayLabel) => {
  if (!text || typeof text !== 'string') return text

  const withLabel =
    label && displayLabel && label !== displayLabel
      ? text.replaceAll(String(label), displayLabel)
      : text

  return formatCurrencyInText(withLabel)
}

export const DashboardTileCoverageRequirementsRow = ({
  Label,
  Value,
  Status,
  HelperText,
  ToolTip,
  accountStatusDetails,
  isLast,
}) => {
  const [isActive, setIsActive] = useState(false)
  const displayLabel = formatRequirementLabel(Label)

  const REQUIREMENTS_STATUS_VARIATIONS = {
    'In Review': {
      icon: Clock,
      iconColor: 'gray',
      value: 'Pending',
    },
    'Up to Date': {
      icon: Success,
      iconColor: 'success',
      value: Value === null ? 'None provided' : Value,
    },
    Unmet: {
      icon: Status === true ? Success : Close02,
      iconColor: Status === true ? 'success' : 'warning',
      value: Value === null ? 'None provided' : Value,
    },
    Cancelled: {
      icon: Error,
      iconColor: 'error',
      value: 'Needs update',
    },
  }

  const accountIconColor = accountStatusDetails?.iconColor
  let variation
  if (accountIconColor === 'error') {
    variation = REQUIREMENTS_STATUS_VARIATIONS.Cancelled
  } else if (accountIconColor === 'gray') {
    variation = REQUIREMENTS_STATUS_VARIATIONS['In Review']
  } else if (Status === true) {
    variation = REQUIREMENTS_STATUS_VARIATIONS['Up to Date']
  } else {
    variation = REQUIREMENTS_STATUS_VARIATIONS.Unmet
  }

  return (
    <Tooltip
      content={
        formatToolTip(ToolTip, Label, displayLabel) ||
        `${displayLabel} must be provided.`
      }
      id={coverageRequirementTooltipId(Label)}
      trigger="click"
      visible={isActive}
      offset={[0, 4]}
      popperOptions={coverageRequirementTooltipPopperOptions}
    >
      <Box
        tabIndex={0}
        borderLeft={isActive ? '5px solid' : 'none'}
        borderColor={isActive ? 'border.brand' : 'transparent'}
        bg={isActive ? 'gray20' : 'transparent'}
        px="2xl"
        onClick={() => setIsActive(true)}
        onBlur={() => setIsActive(false)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.currentTarget.click()
          }
        }}
      >
        <Flex
          justifyContent="space-between"
          borderBottom={isLast ? 'none' : '1px solid'}
          borderColor="border.defaultLight"
          py={HelperText ? '2xl' : '4xl'}
          gap="sm"
        >
          <Flex.Col pl={isActive ? '2xl' : 0} pr={0} minWidth={0}>
            <P
              typeStyle="labelLarge.medium"
              mb="0"
              whiteSpace={['normal', 'nowrap']}
            >
              {displayLabel}
            </P>
            {HelperText && (
              <P typeStyle="tdsSmall.regular" mb="0" color="text.secondary">
                {formatCurrencyInText(HelperText)}
              </P>
            )}
          </Flex.Col>
          <Flex.Col
            colWidth="auto"
            pl={0}
            pr={0}
            display="flex"
            flexDirection="row"
            alignItems="flex-start"
            justifyContent="flex-end"
            gap="md"
          >
            <P
              typeStyle="labelLarge.regular"
              mb="0"
              color="text.secondary"
              textAlign="right"
              whiteSpace="nowrap"
            >
              {formatRequirementValue(variation.value)}
            </P>
            <Icon src={variation.icon} color={variation.iconColor} mt="4px" />
          </Flex.Col>
        </Flex>
      </Box>
    </Tooltip>
  )
}

DashboardTileCoverageRequirementsRow.propTypes = {
  Label: PropTypes.string,
  Value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  Status: PropTypes.bool,
  HelperText: PropTypes.string,
  ToolTip: PropTypes.string,
  accountStatusDetails: PropTypes.object,
  isLast: PropTypes.bool,
}
